import type { StringsShape } from "./strings.en";

/**
 * Spanish UI strings.
 *
 * MUST remain a direct object literal typed `StringsShape` — spreads or
 * intermediate variables would silence TypeScript's excess-property checks,
 * which are what make an extra/misspelled key a compile error.
 *
 * Terminology follows the existing question-forms Spanish content (e.g.
 * "(Obligatorio)") and uses the formal "usted" register throughout.
 */
export const es: StringsShape = {
  signOut: "Cerrar sesión",
  accessDenied: "Acceso denegado",
  accessDeniedMessage:
    "El acceso a RePath Web no está disponible para su perfil. Descargue la aplicación RePath Mobile para iOS o Android.",
  checkInPhotoNote:
    "Al tomar su foto, asegúrese de tener una iluminación adecuada.",
  checkInModalTitle: "Foto de registro",
  questionsModalTitle: "Preguntas",
  updateAnswersModalTitle: "Actualizar respuestas",
  termsModalTitle: "Términos de uso",
  termsModalText:
    "¡Bienvenido a RePath! Revise y acepte nuestros Términos de uso para continuar",
  acceptTermsBtn: "Aceptar términos",
  questionsModalText: "Envíe sus respuestas a las siguientes preguntas",
  updateAnswersModalText:
    "Puede actualizar sus respuestas a las siguientes preguntas en cualquier momento",
  submitting: "Enviando...",
  submitAnswers: "Enviar respuestas",
  save: "Guardar",
  start: "Comenzar",
  answerNow: "Responder ahora",
  nextQuestionsDate: "Fecha de las próximas preguntas",
  questionsDue: "¡Preguntas de registro pendientes!",
  loading: "Cargando...",
  questionAnswers: {
    yes: "Sí",
    no: "No",
    yesNotesPlaceholder: "Explique, por favor...",
    textPlaceholder: "Escriba su respuesta...",
  },
  reportForm: {
    title: "Formulario de informe",
    questionList: "Lista de preguntas",
    closeLabel: "Cerrar formulario de informe",
    welcomeHeading: "Preparémonos para su reunión con su oficial.",
    welcomeBody:
      "Para su cita, debe responder algunas preguntas obligatorias para mantener informado a su oficial.",
    welcomeHint: "¿Necesita regresar? Use la Lista de preguntas.",
    start: "Comenzar",
    stepCounter: "Pregunta %1s/%2s",
    getStarted: "Empezar",
    workflow: {
      saveContinue: "Guardar y continuar",
      changeAnswer: "Cambiar mi respuesta",
      remove: "Eliminar",
      addAnother: "Agregar otro",
      skip: "Omitir",
      errors: {
        email:
          "Ingrese un correo electrónico válido (p. ej. nombre@ejemplo.com).",
        vehicleYear: "Ingrese un año válido de 4 dígitos.",
        ownerName: "Ingrese un nombre — no puede ser solo números.",
      },
    },
    questionMenu: {
      title: "Lista de preguntas",
      closeLabel: "Cerrar lista de preguntas",
      generalSection: "Preguntas generales",
      statusDone: "Completada",
      statusCurrent: "Está aquí",
      statusNotStarted: "Sin comenzar",
    },
    finalStatement: {
      topic: "Envío",
      heading: "¿Está listo para enviar este informe? Confirme y envíe.",
      firstNameLabel: "Nombre",
      lastNameLabel: "Apellido",
      submit: "Enviar informe",
      submitting: "Enviando…",
      successHeading: "Informe enviado",
      successBody: "Sus respuestas han sido enviadas a su oficial.",
      errorHeading: "Error al enviar",
      errorBody:
        "No pudimos enviar su informe. Sus respuestas están guardadas — inténtelo de nuevo.",
      retry: "Intentar de nuevo",
      nameInvalid: "Ingrese un nombre — no puede ser solo números.",
    },
  },
  sessionTimeout: {
    heading: "¿Sigue ahí?",
    body: "Su sesión terminará pronto y se cerrará su sesión. Segundos restantes:",
    continue: "Continuar mi sesión",
  },
  pageNotFound: "Página no encontrada",
  skipToContent: "Saltar al contenido principal",
  pages: {
    dashboard: "Inicio",
    appointments: "Citas",
    documents: "Documentos",
    termsOfUse: "Términos de uso",
    officerProfile: "Mi oficial",
    resources: "Recursos",
    resourceDetail: "Detalles del recurso",
  },
  navbar: {
    openSidebar: "abrir menú lateral",
    closeSidebar: "Cerrar el menú de navegación",
    capturePhoto: "Tomar foto",
    // Must CONTAIN the visible label ("Cerrar sesión") verbatim — SC 2.5.3.
    // "Cerrar la sesión de su cuenta" did not, so a speech-input user saying
    // what they could see could not activate the control.
    signOutAria: "Cerrar sesión de su cuenta",
    navigationMenu: "Menú de navegación",
  },
  login: {
    usernameLabel: "Número de teléfono o correo electrónico",
    usernamePlaceholder: "Ingrese su número de teléfono o correo electrónico",
    phoneValidation:
      "Ingrese un número de teléfono de EE. UU. de 10 dígitos, o un número internacional con su código de país (p. ej. +63 917 555 1234).",
  },
  countdown: {
    today: "hoy",
    oneDay: "1 día",
    days: "%1s días",
    oneDayAgo: "hace 1 día",
    daysAgo: "hace %1s días",
    takesPlaceToday: "Tiene lugar hoy",
    takesPlaceIn: "Tiene lugar en %1s",
    takesPlacePast: "Tuvo lugar %1s",
  },
  dateAtTime: "%1s a las %2s",
  dateTimeAt: "%1s %3s %2s, %4s",
  dateTimeAtRange: "%1s %3s %2s, %4s - %5s",
  documents: {
    myDocuments: "Mis documentos",
    uploadDocuments: "Subir documentos",
    chooseFileLabel: "Elija un documento para subir",
    fileType: "Tipo",
    fileName: "Nombre",
    uploadDate: "Fecha de subida",
    action: "Acción",
    downloadBtn: "Descargar",
    deleteBtn: "Eliminar",
    dragDropText: "Arrastre y suelte sus archivos aquí",
    loadingDocuments: "Cargando documentos...",
    loadDocsError: "Error al cargar los documentos: ",
    downloadDocError: "Error al descargar el documento: ",
  },
  dashboard: {
    greeting: "👋 Hola, %1s",
    greetingFallback: "👋 Hola",
    checkInHeader: "Registro",
    nextCourtDate: "Próxima fecha de corte",
    nextAppointmentDate: "Próxima fecha de cita",
    seeAll: "Ver todas (%1s)",
  },
  appointmentLabels: {
    what: "Qué:",
    when: "Cuándo:",
    where: "Dónde:",
    instructions: "Instrucciones:",
  },
  appointments: {
    apptsHeader: "Citas",
    courtHeader: "Corte",
    courtTileTitle: "Fecha de corte",
    appointmentTileTitle: "Cita",
    upcomingCourtDate: "Próxima fecha de corte el",
    upcomingAppt: "Próxima cita el",
    noUpcomingAppts: "No hay citas próximas",
    noUpcomingCourtDates: "No hay fechas de corte próximas",
    apptsTimeFrame: "%1s Entre %2s - %3s",
  },
  toast: {
    generalError: "Ha ocurrido un error, inténtelo de nuevo.",
    faceNotRecognized: "Rostro no reconocido, inténtelo de nuevo.",
    multipleFacesDetected: "Se detectaron varios rostros, inténtelo de nuevo.",
    noFaceDetected: "No se detectó ningún rostro, inténtelo de nuevo.",
    loadCaseError: "Error al cargar el caso del participante: ",
    checkInPhotoSuccess: "Foto de registro enviada",
    checkInPhotoError:
      "No se pudo enviar la foto de registro, inténtelo de nuevo.",
    questionAnswersSuccess: "Respuestas enviadas correctamente",
    questionAnswersError: "¡Error al enviar las respuestas!",
    uploadDocumentSuccess: "Documento subido correctamente",
    uploadDocumentError: "¡Error al subir el documento!",
    loading: "Espere, por favor...",
    loadAppointmentsError: "Error al cargar las citas",
    loadTermsAndConditionsError:
      "No se pudieron cargar los Términos de uso; actualice la página para intentarlo de nuevo.",
    acceptTermsSuccess: "Términos de uso aceptados",
    acceptTermsError:
      "No se pudieron aceptar los Términos de uso, inténtelo de nuevo.",
  },
  pageContentLoading: "Cargando",
  copyright: "Copyright © eHawk Solutions %1s",
  chat: {
    navTitle: "Hablar con el oficial",
    loadingLabel: "mensajes",
    headerFallback: "Su oficial",
    emptyState: "No hay mensajes todavía. Envíe un mensaje a su oficial.",
    inputPlaceholder: "Escriba un mensaje",
    inputLabel: "Mensaje",
    send: "Enviar",
    sendFailed: "No se pudo enviar el mensaje.",
    retry: "Reintentar",
    typing: "Escribiendo…",
    loadError: "No se pudieron cargar los mensajes, inténtelo de nuevo.",
    messageHistory: "Historial de mensajes",
    loadingOlder: "Cargando mensajes anteriores",
    today: "Hoy",
    yesterday: "Ayer",
    unreadMessages: "mensajes sin leer",
  },
  resources: {
    loading: "Cargando recursos...",
    loadError:
      "No se pudieron cargar los recursos. Inténtelo de nuevo más tarde.",
    empty: "No hay recursos disponibles.",
    categoryLabel: "Categoría",
    allCategories: "Todas",
    keywordsLabel: "Palabras clave:",
    resultCountOne: "Mostrando 1 recurso",
    resultCountMany: "Mostrando %1s recursos",
    resultCountOneInCategory: "Mostrando 1 recurso en %2s",
    resultCountManyInCategory: "Mostrando %1s recursos en %2s",
    searchLabel: "Buscar recursos",
    clearSearch: "Borrar búsqueda",
    clearSearchText: "Borrar el texto de búsqueda",
    searchAllCategories: "Buscar en todas las categorías",
    searchCountOne: "1 recurso coincide con «%3s»",
    searchCountMany: "%1s recursos coinciden con «%3s»",
    searchCountOneInCategory: "1 recurso en %2s coincide con «%3s»",
    searchCountManyInCategory: "%1s recursos en %2s coinciden con «%3s»",
    noMatch: "Ningún recurso coincide con «%3s».",
    noMatchInCategory: "Ningún recurso en %2s coincide con «%3s».",
    allResources: "Todos los recursos",
    notAvailable: "Este recurso ya no está disponible.",
    mainLocation: "Ubicación principal",
    otherLocations: "Otras ubicaciones",
    locationNumber: "Ubicación %1s",
    call: "Llamar al %1s",
    extension: "ext. %1s",
    directionsTo: "Cómo llegar a %1s",
    opensInNewTab: "(se abre en una pestaña nueva)",
  },
};
